import {
  type CollectionType,
  type DirectusClient,
  type GetCollection,
  type Query,
  type RegularCollections,
  type RestClient,
  type UnpackList,
  aggregate,
  readItems,
} from '@directus/sdk';
import { type SortSpec } from '@src/types/sort';
import { useCallback, useState } from 'react';
import { usePagination } from '@hooks/usePagination.ts';
import { usePromise } from '@hooks/usePromise.ts';
import type { ItemTypeFromQuery } from '@src/components/table/types';

export const usePaginatedDirectusData = <
  Schema extends object,
  Collection extends RegularCollections<Schema>,
  TQuery extends Query<Schema, CollectionType<Schema, Collection>>,
  TItem = ItemTypeFromQuery<Schema, Collection, TQuery>,
>({
  itemsPerPage,
  directusClient,
  collection,
  query,
  initialSort,
}: {
  itemsPerPage: number;
  directusClient: DirectusClient<Schema> & RestClient<Schema>;
  collection: Collection;
  query?: Omit<TQuery, 'sort' | 'limit' | 'page' | 'offset'>;
  initialSort?: SortSpec;
}) => {
  const [reloadWithoutCache, setReloadWithoutCache] = useState(false);

  const {
    data: count,
    error: countError,
    loading: countLoading,
    refresh: refreshCount,
  } = usePromise(
    () =>
      directusClient
        .request(
          aggregate(collection, {
            query: query?.filter
              ? ({ filter: query.filter } as Query<
                  Schema,
                  Collection extends keyof Schema
                    ? UnpackList<GetCollection<Schema, Collection>>
                    : object
                >)
              : {},
            // Related Issue: https://github.com/directus/directus/issues/23395
            // We can't use count as this returns the wrong number
            aggregate: { countDistinct: '*' },
            ...(reloadWithoutCache ? { date: new Date().toISOString() } : {}),
          }),
        )
        .then((result) => {
          setReloadWithoutCache(false);
          return result;
        }),
    { setLoadingStateOnRefresh: true },
    [directusClient, collection, query],
  );
  // @ts-expect-error the directus sdk return some weird return value for count distinct, seems to be not typed correctly
  const totalCount = parseInt(String(count?.[0]?.countDistinct['*'] ?? '0'));

  const { currentPage, numberOfAvailablePages, setCurrentPage } = usePagination(
    { totalCount, itemsPerPage },
  );

  const [sort, setSort] = useState<SortSpec | null>(initialSort ?? null);

  const {
    data,
    error: dataError,
    loading: dataLoading,
    refresh: refreshData,
  } = usePromise<TItem[]>(
    () =>
      directusClient
        .request<TItem[]>(
          readItems(collection, {
            ...query,
            page: currentPage + 1,
            limit: itemsPerPage,
            sort: sort
              ? ([
                  `${sort.direction === 'ASC' ? '' : '-'}${sort.fieldName}`,
                ] as TQuery['sort'])
              : [],
            ...(reloadWithoutCache ? { date: new Date().toISOString() } : {}),
          }),
        )
        .then((result) => {
          setReloadWithoutCache(false);
          return result;
        }),
    { setLoadingStateOnRefresh: true },
    [
      directusClient,
      collection,
      query,
      currentPage,
      itemsPerPage,
      sort,
      reloadWithoutCache,
    ],
  );

  const reload = useCallback(
    (withoutCache: boolean) => {
      setReloadWithoutCache(withoutCache);
      refreshCount();
      refreshData();
    },
    [refreshCount, refreshData],
  );

  return {
    data,
    error: dataError ?? countError,
    loading: dataLoading || countLoading,
    totalCount,
    currentPage,
    numberOfAvailablePages,
    setCurrentPage,
    sort,
    setSort,
    reload,
  };
};
